import { useEffect, useMemo, useState } from "react";
import { useCatalog, useDeleteReport, useReports, useSaveReport } from "../../api/hooks";
import type { ReportDef } from "../../api/types";
import { usePendingOpenFile, usePublishActiveFile } from "../../app/activeFile";
import { useSession } from "../../app/store";
import { Split } from "../../components/Split";
import { Empty, Modal, Spinner, errText } from "../../components/ui";
import { OutputsEditor } from "./OutputsEditor";
import { PreviewPanel } from "./PreviewPanel";
import { ValuesTable } from "./ValuesTable";
import { emptyOutput, emptyReport, normaliseReport } from "./model";
import "./reports.css";

type Tab = "general" | "values" | "outputs";

export function ReportsPage() {
  const projectId = useSession((s) => s.projectId);
  const toast = useSession((s) => s.toast);
  const reports = useReports(projectId);
  const catalog = useCatalog(projectId);
  const save = useSaveReport(projectId);
  const del = useDeleteReport(projectId);

  const [selected, setSelected] = useState<string | null>(null);
  const [draft, setDraft] = useState<ReportDef | null>(null);
  const [savedJson, setSavedJson] = useState<string>("");
  const [tab, setTab] = useState<Tab>("values");
  const [outIdx, setOutIdx] = useState(0);
  const [templateDraft, setTemplateDraft] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [filter, setFilter] = useState("");

  const entries = reports.data ?? [];
  const entry = entries.find((e) => e.report?.name === selected) ?? null;

  useEffect(() => {
    if (!entries.length || reports.isFetching) return;
    if (!selected || !entries.some((e) => e.report?.name === selected)) {
      const first = entries.find((e) => e.report)?.report?.name ?? null;
      setSelected(first);
    }
  }, [entries, reports.isFetching, selected]);
  usePublishActiveFile(entry?.file ?? (selected ? `reports/${selected}.report.yaml` : null));
  usePendingOpenFile("reports/", (path) => {
    const byFile = entries.find((e) => e.file === path)?.report?.name;
    setSelected(byFile ?? path.split("/").pop()!.replace(/\.report\.ya?ml$/, "").replace(/\.ya?ml$/, ""));
  });

  useEffect(() => {
    if (entry?.report) {
      const json = JSON.stringify(entry.report);
      if (json !== savedJson) {
        setDraft(structuredClone(entry.report));
        setSavedJson(json);
        setOutIdx(0);
        setTemplateDraft(null);
      }
    } else if (!entry) {
      setDraft(null);
      setSavedJson("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry]);

  const dirty = draft ? JSON.stringify(draft) !== savedJson || templateDraft !== null : false;
  const triggers = useMemo(() => (catalog.data?.triggers ?? []).map((t) => t.name), [catalog.data]);
  const labelChoices = useMemo(() => {
    const set = new Set<string>();
    for (const t of draft?.triggers ?? []) for (const l of catalog.data?.snapshot_labels?.[t] ?? []) set.add(l);
    return [...set];
  }, [draft?.triggers, catalog.data]);

  const patch = (p: Partial<ReportDef>) => setDraft((d) => (d ? { ...d, ...p } : d));

  const doSave = async () => {
    if (!draft || !selected) return;
    let rep = normaliseReport(draft);
    if (templateDraft !== null && rep.outputs[outIdx]) {
      rep = { ...rep, outputs: rep.outputs.map((o, i) => (i === outIdx ? { ...o, template: null, template_text: templateDraft } : o)) };
    }
    try {
      const r = await save.mutateAsync({ name: selected, report: rep });
      setSavedJson(JSON.stringify(r.report));
      setDraft(structuredClone(r.report));
      setTemplateDraft(null);
      setSelected(r.report.name);
      toast(`Saved ${r.file}`, "success");
    } catch (e) {
      toast(errText(e), "error");
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (dirty) void doSave();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (!projectId) return <Empty title="Select a project">Reports are stored per project under reports/*.report.yaml.</Empty>;

  const visible = entries.filter((e) => !filter || (e.report?.name ?? e.file).toLowerCase().includes(filter.toLowerCase()));

  return (
    <>
    <Split id="reports" className="reports-page" sizes={[220, "1fr", "1fr"]} minSizes={[140, 320, 240]} collapsible={[true, false, true]}>
      <div className="pane">
        <div className="pane-head">
          Reports
          <span className="spacer" />
          <button className="btn ghost sm" title="New report" onClick={() => setCreating(true)}>
            ＋
          </button>
        </div>
        <div style={{ padding: "4px 6px" }}>
          <input className="inp sm" placeholder="filter…" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ width: "100%" }} />
        </div>
        <div className="list">
          {reports.isLoading ? <Spinner label="loading" /> : null}
          {visible.map((e) => (
            <div
              key={e.file}
              className={`list-item${e.report?.name === selected ? " sel" : ""}${e.error ? " bad" : ""}`}
              onClick={() => e.report && setSelected(e.report.name)}
              title={e.error ?? e.file}
            >
              <span className="name">{e.report?.name ?? e.file}</span>
              {e.report && !e.report.enabled ? <span className="badge warn">off</span> : null}
              {e.error ? <span className="badge err">!</span> : null}
              <span className="dim small">{e.report?.outputs.length ?? 0}</span>
            </div>
          ))}
          {!reports.isLoading && !entries.length ? <div className="dim small" style={{ padding: 10 }}>No reports yet.</div> : null}
        </div>
      </div>

      {!draft ? (
        <Empty title="No report selected">Create a report or pick one on the left.</Empty>
      ) : (
        <div className="pane">
          <div className="pane-head">
            <span className="mono">{draft.name}</span>
            {dirty ? <span className="badge warn">unsaved</span> : null}
            <span className="spacer" />
            <button className="btn sm" onClick={() => setShowPreview(!showPreview)}>
              {showPreview ? "Hide preview" : "Preview"}
            </button>
            <button
              className="btn sm danger"
              onClick={async () => {
                if (!selected || !confirm(`Delete report "${selected}"?`)) return;
                await del.mutateAsync(selected);
                setSelected(null);
              }}
            >
              Delete
            </button>
            <button className="btn sm primary" disabled={!dirty || save.isPending} onClick={doSave}>
              {save.isPending ? <Spinner /> : "Save"}
            </button>
          </div>
          <div className="tabs">
            {(["general", "values", "outputs"] as Tab[]).map((t) => (
              <button key={t} className={tab === t ? "on" : ""} onClick={() => setTab(t)}>
                {t}
                {t === "values" ? ` (${draft.values.length})` : t === "outputs" ? ` (${draft.outputs.length})` : ""}
              </button>
            ))}
          </div>
          <div style={{ flex: 1, minHeight: 0, overflow: "auto", display: "flex", flexDirection: "column" }}>
            {tab === "general" ? (
              <div className="form" style={{ padding: 12, maxWidth: 720 }}>
                <label>
                  Name
                  <input className="inp mono" value={draft.name} onChange={(e) => patch({ name: e.target.value.replace(/[^\w.-]/g, "_") })} />
                </label>
                <label>
                  Description
                  <textarea className="inp" rows={2} value={draft.description} onChange={(e) => patch({ description: e.target.value })} />
                </label>
                <label>
                  Triggers <span className="dim small">– which triggers' snapshots feed this report</span>
                  <div className="chips">
                    {triggers.map((t) => {
                      const on = draft.triggers.includes(t);
                      return (
                        <button
                          key={t}
                          className={`chip${on ? " on" : ""}`}
                          onClick={() => patch({ triggers: on ? draft.triggers.filter((x) => x !== t) : [...draft.triggers, t] })}
                        >
                          {t}
                        </button>
                      );
                    })}
                    {!triggers.length ? <span className="dim small">no triggers in project</span> : null}
                  </div>
                  <input
                    className="inp mono sm"
                    placeholder="or type names, comma separated"
                    value={draft.triggers.filter((t) => !triggers.includes(t)).join(", ")}
                    onChange={(e) =>
                      patch({
                        triggers: [
                          ...draft.triggers.filter((t) => triggers.includes(t)),
                          ...e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                        ],
                      })
                    }
                  />
                </label>
                <label>
                  Labels <span className="dim small">– restrict to snapshot labels (empty = all)</span>
                  <div className="chips">
                    {labelChoices.map((l) => {
                      const on = (draft.labels ?? []).includes(l);
                      return (
                        <button
                          key={l}
                          className={`chip${on ? " on" : ""}`}
                          onClick={() => patch({ labels: on ? (draft.labels ?? []).filter((x) => x !== l) : [...(draft.labels ?? []), l] })}
                        >
                          {l}
                        </button>
                      );
                    })}
                  </div>
                  <input
                    className="inp mono sm"
                    value={(draft.labels ?? []).join(", ")}
                    placeholder="all labels"
                    onChange={(e) => patch({ labels: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                  />
                </label>
                <div className="row">
                  <label>
                    Max per flight
                    <input
                      className="inp"
                      type="number"
                      min={1}
                      value={draft.max_per_flight ?? ""}
                      placeholder="∞"
                      onChange={(e) => patch({ max_per_flight: e.target.value === "" ? null : Number(e.target.value) })}
                      style={{ width: 100 }}
                    />
                  </label>
                  <label className="row" style={{ alignSelf: "end", gap: 6 }}>
                    <input type="checkbox" checked={draft.enabled} onChange={(e) => patch({ enabled: e.target.checked })} /> enabled
                  </label>
                </div>
                <div className="dim small">
                  File: <code>{entry?.file}</code>
                </div>
              </div>
            ) : null}
            {tab === "values" ? <ValuesTable values={draft.values} onChange={(values) => patch({ values })} catalog={catalog.data} triggers={draft.triggers} /> : null}
            {tab === "outputs" ? (
              <OutputsEditor
                outputs={draft.outputs}
                onChange={(outputs) => patch({ outputs })}
                catalog={catalog.data}
                projectId={projectId}
                selectedIndex={Math.min(outIdx, Math.max(0, draft.outputs.length - 1))}
                onSelect={(i) => (setOutIdx(i), setTemplateDraft(null))}
                templateDraft={templateDraft}
                onTemplateDraft={setTemplateDraft}
              />
            ) : null}
          </div>
        </div>
      )}

      {showPreview && draft ? (
        <div className="pane">
          <div className="pane-head">Preview</div>
          <PreviewPanel
            projectId={projectId}
            report={draft}
            outputName={tab === "outputs" ? draft.outputs[outIdx]?.name ?? null : null}
            templateDraft={tab === "outputs" ? templateDraft : null}
          />
        </div>
      ) : null}
    </Split>

      {creating ? (
        <NewReportDialog
          existing={entries.map((e) => e.report?.name ?? "")}
          triggers={triggers}
          onClose={() => setCreating(false)}
          onCreate={async (rep) => {
            try {
              const r = await save.mutateAsync({ name: rep.name, report: rep });
              setCreating(false);
              setSelected(r.report.name);
              setTab("values");
            } catch (e) {
              toast(errText(e), "error");
            }
          }}
        />
      ) : null}
    </>
  );
}

function NewReportDialog({
  existing,
  triggers,
  onClose,
  onCreate,
}: {
  existing: string[];
  triggers: string[];
  onClose: () => void;
  onCreate: (r: ReportDef) => void;
}) {
  const [name, setName] = useState("");
  const [trig, setTrig] = useState<string[]>([]);
  const [renderer, setRenderer] = useState<"jinja2" | "legacy_bracket" | "python">("jinja2");
  const clean = name.trim().replace(/[^\w.-]/g, "_");
  const taken = existing.includes(clean);
  return (
    <Modal title="New report" onClose={onClose}>
      <div className="form">
        <label>
          Name
          <input className="inp mono" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="takeoff_custom" />
          {taken ? <span className="small err-text">a report with this name already exists</span> : null}
        </label>
        <label>
          Triggers
          <div className="chips">
            {triggers.map((t) => (
              <button key={t} className={`chip${trig.includes(t) ? " on" : ""}`} onClick={() => setTrig(trig.includes(t) ? trig.filter((x) => x !== t) : [...trig, t])}>
                {t}
              </button>
            ))}
          </div>
        </label>
        <label>
          First output
          <select className="inp" value={renderer} onChange={(e) => setRenderer(e.target.value as typeof renderer)}>
            <option value="jinja2">Jinja2 template (fully custom text/CSV/JSON/HTML)</option>
            <option value="legacy_bracket">Bracket template (SAGE / DIG / TXT)</option>
            <option value="python">Python render() function</option>
          </select>
        </label>
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={!clean || taken}
            onClick={() => {
              const rep = emptyReport(clean);
              rep.triggers = trig;
              rep.outputs[0] = {
                ...emptyOutput("text", renderer),
                function: renderer === "python" ? "renderers/custom.py:render" : null,
              };
              onCreate(rep);
            }}
          >
            Create
          </button>
        </div>
      </div>
    </Modal>
  );
}
